/* Message bubble — with channel indicator, edit, translate + approve/decline. */
import React, { useEffect, useMemo, useState } from "react";
import i18n from "i18next";
import {
    Send, Sparkles, Loader2, Check,
    ThumbsUp, ThumbsDown, Edit3, Save, Languages, BrainCircuit, Lightbulb,
} from "lucide-react";
import { INTENT_STYLE, LANGUAGES, channelMeta, substituteVars } from "./constants";

export default function MessageBubble({ msg, prospect, onApprove, onReject, onSaveEdit, onTranslate, onRegenerate, onSend, onSendNow, onSubmit }) {
    const isOut = msg.direction === "out";
    const ch = channelMeta(msg.channel);
    const ChIcon = ch.Icon;
    const at = msg.at ? new Date(msg.at).toLocaleString("nl-BE", { dateStyle: "short", timeStyle: "short" }) : "";
    const needsApproval = isOut && msg.status === "pending_approval";
    const isDraft = isOut && msg.status === "draft";
    const isApproved = isOut && msg.status === "approved";
    const isScheduled = isOut && msg.status === "scheduled";
    const wasRejected = isOut && msg.status === "rejected";
    const canEdit = isOut && msg.status !== "sent" && msg.status !== "rejected";

    // Substitute {{first_name}} / {{company}} placeholders for readable copy.
    const renderedBody = useMemo(() => substituteVars(msg.body || "", prospect), [msg.body, prospect]);
    const renderedSubject = useMemo(() => substituteVars(msg.subject || "", prospect), [msg.subject, prospect]);

    // Editing state
    const [editing, setEditing] = useState(false);
    const [draftBody, setDraftBody] = useState(msg.body || "");
    const [draftSubject, setDraftSubject] = useState(msg.subject || "");
    const [saving, setSaving] = useState(false);
    const [translating, setTranslating] = useState(null); // lang code being translated
    const [regenerating, setRegenerating] = useState(false);
    const [sendingMsg, setSendingMsg] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { setDraftBody(msg.body || ""); setDraftSubject(msg.subject || ""); }, [msg.body, msg.subject]);

    const startEdit = () => { setDraftBody(msg.body || ""); setDraftSubject(msg.subject || ""); setEditing(true); };
    const cancelEdit = () => setEditing(false);
    const saveEdit = async () => {
        setSaving(true);
        try {
            // Use msg.msg_id (the real outreach _id) — msg.id has the "-out" suffix
            // added by the inbox aggregator and would 404 against /outreach/{id}.
            await onSaveEdit?.(msg.msg_id || msg.id, {
                body: draftBody,
                subject: msg.channel === "email" ? draftSubject : undefined,
            });
            setEditing(false);
        } finally { setSaving(false); }
    };
    const doTranslate = async (lang) => {
        setTranslating(lang);
        try { await onTranslate?.(msg.msg_id || msg.id, lang); }
        finally { setTranslating(null); }
    };
    const doRegenerate = async () => {
        setRegenerating(true);
        try { await onRegenerate?.(msg.msg_id || msg.id); }
        finally { setRegenerating(false); }
    };
    const doSend = async () => {
        setSendingMsg(true);
        try { await onSend?.(msg.msg_id || msg.id); }
        finally { setSendingMsg(false); }
    };
    const doSendNow = async () => {
        setSendingMsg(true);
        try { await onSendNow?.(msg.msg_id || msg.id); }
        finally { setSendingMsg(false); }
    };
    const doSubmit = async () => {
        setSubmitting(true);
        try { await onSubmit?.(msg.msg_id || msg.id); }
        finally { setSubmitting(false); }
    };

    return (
        <div className={`flex ${isOut ? "justify-end" : "justify-start"} min-w-0`} data-testid={`bubble-${msg.id}`}>
            <div className={`max-w-[92%] min-w-0 ${isOut ? "items-end text-right" : "items-start text-left"} flex flex-col`}>
                {/* Prominent channel badge — always visible above every bubble */}
                <span className={`inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${ch.bg} ${ch.fg} ${ch.border} mb-1`}
                    data-testid={`bubble-channel-${msg.id}`} title={ch.label}>
                    <ChIcon size={10} /> {ch.label}
                    {isOut && <span className="opacity-60">· Agent</span>}
                    {(msg.sent_via || "").startsWith("scaliq") && (
                        <span className="text-[#818cf8] normal-case tracking-normal font-semibold"
                            data-testid={`bubble-scaliq-${msg.id}`}>· ScalIQ</span>
                    )}
                    {msg.agent_id === "scaliq_reply_agent" && (
                        <span className="text-[#818cf8] normal-case tracking-normal font-semibold"
                            data-testid={`bubble-replyagent-${msg.id}`}>· Reply-agent</span>
                    )}
                    {msg.is_followup && (
                        <span className="text-[#a78bfa] normal-case tracking-normal font-semibold"
                            data-testid={`bubble-followup-${msg.id}`}>
                            · Follow-up #{msg.followup_number || 1}
                        </span>
                    )}
                </span>
                <div className={`rounded-2xl px-4 py-2.5 border-l-[3px] break-words min-w-0 ${isOut
                    ? "bg-[var(--jr-info)]/85 text-white"
                    : "bg-[var(--jr-surface-2)] border border-white/10 text-[var(--jr-text-2)]"} ${editing ? "w-full" : ""}`}
                    style={{ borderLeftColor: ch.tint }}>
                    {editing ? (
                        <div className="text-left space-y-2">
                            {msg.channel === "email" && (
                                <input value={draftSubject} onChange={(e) => setDraftSubject(e.target.value)}
                                    placeholder={i18n.t("inbox.subject_lbl")}
                                    data-testid={`edit-subject-${msg.id}`}
                                    className="w-full bg-black/25 border border-white/15 rounded-md px-2.5 py-1.5 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                            )}
                            <textarea value={draftBody} onChange={(e) => setDraftBody(e.target.value)}
                                rows={9} placeholder={i18n.t("inbox.write_msg")}
                                data-testid={`edit-body-${msg.id}`}
                                className="w-full min-w-[320px] bg-black/25 border border-white/15 rounded-md px-3 py-2 text-sm text-white leading-relaxed outline-none focus:border-[var(--jr-brand)] resize-y" />
                            <div className="flex items-center justify-end gap-2">
                                <button onClick={cancelEdit} data-testid={`edit-cancel-${msg.id}`}
                                    className="text-[11px] px-3 py-1 rounded-full border border-white/15 text-[var(--jr-text-2)] hover:text-white hover:bg-white/5">
                                    {i18n.t("common.cancel")}
                                </button>
                                <button onClick={saveEdit} disabled={saving} data-testid={`edit-save-${msg.id}`}
                                    className="inline-flex items-center gap-1.5 text-[11px] px-3 py-1 rounded-full bg-[var(--jr-brand)] text-black font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                                    {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} {i18n.t("common.save")}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {renderedSubject && <div className="text-[11px] font-mono uppercase tracking-[0.14em] mb-1 opacity-70 break-words">Subj: {renderedSubject}</div>}
                            <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{renderedBody}</p>
                        </>
                    )}
                </div>
                <div className="mt-1 flex items-center gap-2 text-[10px] text-[var(--jr-text-3)] flex-wrap">
                    <span>{isOut && msg.status ? msg.status : "received"}</span>
                    <span>·</span>
                    <span>{at}</span>
                </div>

                {/* Sprint 6 · #20 — AI-intent van de reply + gesuggereerde vervolgactie */}
                {!isOut && msg.reply_intent && (
                    <div className="mt-1.5 flex items-center gap-1.5 flex-wrap" data-testid={`intent-row-${msg.id}`}>
                        <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-medium ${INTENT_STYLE[msg.reply_intent] || INTENT_STYLE.question}`}
                            title={i18n.t("intent.label")}
                            data-testid={`intent-badge-${msg.id}`}>
                            <BrainCircuit size={10} /> {i18n.t(`intent.${msg.reply_intent}`)}
                            {msg.reply_intent_confidence != null && (
                                <span className="opacity-70">· {Math.round(msg.reply_intent_confidence * 100)}%</span>
                            )}
                        </span>
                        {msg.reply_intent_action && (
                            <span className="inline-flex items-center gap-1 text-[10px] text-[var(--jr-text-3)]" data-testid={`intent-action-${msg.id}`}>
                                <Lightbulb size={10} className="text-[#eab308]" /> {msg.reply_intent_action}
                            </span>
                        )}
                    </div>
                )}

                {/* Action row — edit + translate flags (visible for editable outgoing msgs) */}
                {canEdit && !editing && (
                    <div className="mt-1.5 flex items-center gap-1.5 flex-wrap" data-testid={`action-row-${msg.id}`}>
                        <button onClick={startEdit} data-testid={`edit-${msg.id}`}
                            title={i18n.t("inbox.edit_msg_tip")}
                            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-white/12 text-[var(--jr-text-2)] hover:bg-white/5 hover:text-white">
                            <Edit3 size={10} /> {i18n.t("common.edit")}
                        </button>
                        <button onClick={doRegenerate} disabled={regenerating} data-testid={`regenerate-${msg.id}`}
                            title={i18n.t("inbox.regen_tip")}
                            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-[var(--jr-brand)]/40 text-[var(--jr-brand)] hover:bg-[var(--jr-brand)]/10 disabled:opacity-50">
                            {regenerating ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />} {i18n.t("inbox.regen_btn")}
                        </button>
                        <span className="text-[10px] text-[var(--jr-text-3)] inline-flex items-center gap-1">
                            <Languages size={10} /> {i18n.t("inbox.translate_lbl")}:
                        </span>
                        {LANGUAGES.map((lang) => (
                            <button key={lang.code} onClick={() => doTranslate(lang.code)}
                                disabled={translating !== null}
                                data-testid={`translate-${lang.code}-${msg.id}`}
                                title={i18n.t("inbox.translate_to", { lang: lang.label })}
                                className="text-base leading-none px-1.5 py-0.5 rounded-full border border-white/12 hover:bg-white/5 disabled:opacity-40">
                                {translating === lang.code ? <Loader2 size={10} className="animate-spin" /> : lang.flag}
                            </button>
                        ))}
                    </div>
                )}

                {/* Send call-to-action — approved messages are ready to go out */}
                {isApproved && !editing && (
                    <div className="mt-2 flex items-center gap-2" data-testid={`send-row-${msg.id}`}>
                        <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[#22c55e]">
                            {i18n.t("inbox.approved_ready")}
                        </span>
                        <button onClick={doSend} disabled={sendingMsg} data-testid={`send-${msg.id}`}
                            title={i18n.t("inbox.send_tip")}
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[var(--jr-brand)] text-black hover:bg-[var(--jr-brand-2)] font-medium disabled:opacity-50">
                            {sendingMsg ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} {i18n.t("inbox.send_btn")}
                        </button>
                    </div>
                )}

                {/* Scheduled — show slot + allow force-send */}
                {isScheduled && !editing && (
                    <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid={`sendnow-row-${msg.id}`}>
                        <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-info)]">
                            {i18n.t("inbox.scheduled_at", {
                                time: msg.scheduled_send_at
                                    ? new Date(msg.scheduled_send_at).toLocaleString("nl-BE", { dateStyle: "short", timeStyle: "short" })
                                    : "—",
                            })}
                        </span>
                        <button onClick={doSendNow} disabled={sendingMsg} data-testid={`send-now-${msg.id}`}
                            title={i18n.t("inbox.send_now_tip")}
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[var(--jr-brand)]/50 text-[var(--jr-brand)] hover:bg-[var(--jr-brand)]/10 font-medium disabled:opacity-50">
                            {sendingMsg ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} {i18n.t("inbox.send_now_btn")}
                        </button>
                    </div>
                )}

                {/* Approve / Decline call-to-action row (thumbs icons) for pending msgs */}
                {needsApproval && !editing && (
                    <div className="mt-2 flex items-center gap-2" data-testid={`approve-row-${msg.id}`}>
                        <button onClick={() => onReject?.(msg)} data-testid={`reject-${msg.id}`}
                            title={i18n.t("inbox.reject_reason_tip")}
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[var(--jr-error)]/50 text-[var(--jr-error)] hover:bg-[var(--jr-error)]/10 font-medium">
                            <ThumbsDown size={12} /> {i18n.t("common.reject")}
                        </button>
                        <button onClick={() => onApprove?.(msg.msg_id || msg.id)} data-testid={`approve-${msg.id}`}
                            title={i18n.t("inbox.approve_send_tip")}
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#22c55e] text-black hover:bg-[#16a34a] font-medium">
                            <ThumbsUp size={12} /> {i18n.t("common.approve")}
                        </button>
                    </div>
                )}
                {isDraft && !editing && (
                    <div className="mt-2 flex items-center gap-2" data-testid={`draft-row-${msg.id}`}>
                        <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-amber)]">
                            Draft
                        </span>
                        <button onClick={doSubmit} disabled={submitting} data-testid={`submit-${msg.id}`}
                            title={i18n.t("inbox.submit_tip")}
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[var(--jr-amber)] text-black hover:bg-[var(--jr-amber)]/85 font-medium disabled:opacity-50">
                            {submitting ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} {i18n.t("inbox.submit_btn")}
                        </button>
                    </div>
                )}
                {wasRejected && (
                    <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-error)]" data-testid={`rejected-badge-${msg.id}`}>
                        {i18n.t("inbox.rejected_not_sent")}
                        {msg.rejection_reason && <span className="normal-case tracking-normal ml-1 text-[var(--jr-text-3)]">&ldquo;{msg.rejection_reason.slice(0, 80)}&rdquo;</span>}
                    </div>
                )}
            </div>
        </div>
    );
}
